import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

function formatError(detail) {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-sm">
        <h1 className="font-serif text-3xl font-light text-center mb-8">Inloggen</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 px-4 py-2.5 rounded-lg">{error}</div>}
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              data-testid="login-email"
              type="email" value={email} onChange={e => setEmail(e.target.value)} required
              className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Wachtwoord</label>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)} required
                autoComplete="current-password"
                className="w-full px-4 py-2.5 pr-10 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                type="button"
                data-testid="login-password-toggle"
                aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword(s => !s)}
                className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-right mt-1">
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password-link"
                className="text-xs text-muted-foreground hover:text-primary hover:underline"
              >
                Wachtwoord vergeten?
              </Link>
            </div>
          </div>
          <button
            data-testid="login-submit"
            type="submit" disabled={loading}
            className="w-full bg-primary text-primary-foreground rounded-full py-3 font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? 'Bezig...' : 'Inloggen'}
          </button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-6">
          Nog geen account? <Link to="/register" className="text-primary font-medium hover:underline">Registreren</Link>
        </p>
      </div>
    </div>
  );
}
